import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

const T = {
  navy: "#0d1b2a", gold: "#c9a96e", slateLt: "#9ca8b7", redTx: "#9b2c2c",
  peach: "#ffebd3", redBg: "#fef2f2", redBd: "#fecaca",
};

const fmtM = (v?: number | string) => {
  const n = Number(v ?? 0);
  return n ? "$" + n.toLocaleString("en-CA") : "—";
};

/* ─── Inline Price Display (for list rows) ─── */
interface InlineProps {
  price?: number;
  basePrice?: number | null;
}

export const PriceInline: React.FC<InlineProps> = ({ price, basePrice }) => {
  const hasBase = basePrice != null && basePrice > 0;
  const differs = hasBase && Number(price) !== Number(basePrice);

  return (
    <Box sx={{ textAlign: "right", flexShrink: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, justifyContent: "flex-end" }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.navy }}>{fmtM(price)}</Typography>
        {differs && (
          <Tooltip title="Selling price differs from base price" arrow>
            <WarningAmberRoundedIcon sx={{ fontSize: 13, color: "#e67e22", ml: 0.3 }} />
          </Tooltip>
        )}
      </Box>
      {differs && (
        <Typography sx={{ fontSize: 10, color: T.redTx, fontWeight: 500, lineHeight: 1, mt: 0.2 }}>
          Base: {fmtM(basePrice)}
        </Typography>
      )}
    </Box>
  );
};

/* ─── Price Card (for right-side preview panel) ─── */
interface CardProps {
  price?: number;
  basePrice?: number | null;
  pricingType?: string;
}

export const PriceCard: React.FC<CardProps> = ({ price, basePrice, pricingType }) => {
  const hasBase = basePrice != null && basePrice > 0;
  const differs = hasBase && Number(price) !== Number(basePrice);

  return (
    <Box
      sx={{
        bgcolor: differs ? T.redBg : T.peach,
        borderRadius: "12px",
        p: 2,
        mb: 2,
        border: differs ? `1.5px solid ${T.redBd}` : "1.5px solid transparent",
        transition: "all 0.25s ease",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy }}>
            Selling Price
          </Typography>
          {differs && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mt: 0.5 }}>
              <WarningAmberRoundedIcon sx={{ fontSize: 13, color: "#e67e22" }} />
              <Typography sx={{ fontSize: 10, color: "#e67e22", fontWeight: 500 }}>
                Differs from base price
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography sx={{ fontSize: 28, fontWeight: 800, color: T.navy, lineHeight: 1 }}>
            {fmtM(price)}
          </Typography>
          <Typography sx={{ fontSize: 10, color: T.slateLt, mt: 0.2 }}>
            {pricingType === "pp" ? "per person" : pricingType === "flat" ? "flat fee" : "included"}
          </Typography>
        </Box>
      </Box>

      {hasBase && (
        <Box sx={{
          mt: 1.5, pt: 1.2, borderTop: `1px solid ${differs ? T.redBd : T.gold}30`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <Typography sx={{ fontSize: 11, fontWeight: 500, color: T.slateLt }}>Base Price</Typography>
          <Typography sx={{
            fontSize: 15, fontWeight: 700,
            color: differs ? T.redTx : T.navy,
          }}>
            {fmtM(basePrice)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
